import React from 'react';
import PropTypes from 'prop-types';
import SidebarMenuGroup from './SidebarMenuGroup';
import SidebarRoleSwitcher from './SidebarRoleSwitcher';
import { WindowHelperWrapper } from "../util/WindowHelper";
import { cloneDeep } from 'lodash';

const EXPANDED_SIDEBAR_BREAKPOINT = 1080;

const SCORM_MANAGER_BLACKLIST = Object.freeze([
  /cportal\/teams/,
  /cportal\/user_uploads/,
  /cportal\/rosters/,
  /cportal\/categories/,
  /cportal\/assignments/,
  /cportal\/templates/,
  /cportal\/training_periods/,
  /cportal\/identity_providers/,
  /cportal\/data_integration/,
  /cportal\/webhooks/
]);

export default class SidebarMenu extends React.Component {
  static propTypes = {
    currentRuleSets: PropTypes.array,
    currentUrl: PropTypes.string,
    menuData: PropTypes.shape({
      current_menu_item_permitted: PropTypes.bool,
      permitted_whitelist: PropTypes.arrayOf(PropTypes.string),
      submenu_items: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.string,
        font_family: PropTypes.string,
        title: PropTypes.string.isRequired,
        target_url: PropTypes.string.isRequired,
        id: PropTypes.string,
        submenu_items: PropTypes.arrayOf(PropTypes.shape({
          title: PropTypes.string.isRequired,
          target_url: PropTypes.string.isRequired
        }))
      }))
    }),
    windowOverride: PropTypes.object
  };

  constructor(props) {
    super(props);
    const serverSide = typeof window === 'undefined';
    this.windowHelper = WindowHelperWrapper(props.windowOverride);
    props.menuData.submenu_items.forEach(item => {
      if (item.submenu_items) {
        item.showChildren = item.submenu_items.some((sub_item) => sub_item.target_url && (sub_item.target_url.split('#')[0] === props.currentUrl));
      }
    })
    this.state = {
      serverSide,
      menuData: props.menuData,
      currentUrl: props.currentUrl
    };
  }

  componentDidMount() {
    if (!this.state.serverSide) {
      // if scorm manager and on a blacklisted page, redirect back to home page
      const { currentRuleSets = [] } = this.props;
      const activeRuleSet = currentRuleSets.find(currentRuleSet => currentRuleSet.active) || {};
      if (this.isScormManagerAndOnBlacklist(activeRuleSet.name)) {
        this.redirectToInterstitial(activeRuleSet.name);
      }

      // menuData.current_menu_item_permitted only is valid for pages that
      // appear in the sidebar menu data
      // check the whitelist to see if we can safely perform this redirect
      // if (!this.pathIsWhitelisted()) {
      //   const { currentRuleSets = [], menuData = {} } = this.props;
      //   if (!menuData.current_menu_item_permitted) {
      //     const redirect = currentRuleSets.find(currentRuleSet => currentRuleSet.active)?.interstitial_redirect;
      //     if (redirect) {
      //       window.location.href = redirect;
      //     }
      //   }
      // }
    }
  }

  pathIsWhitelisted = () => {
    const path = window.location.pathname;
    return (this.props.menuData.permitted_whitelist || []).some(permittedPath => path.includes(permittedPath))
  }

  pathOnScormManagerBlackList = () => {
    const url = window.location.pathname + window.location.search + window.location.hash;
    return SCORM_MANAGER_BLACKLIST.some(regexp => regexp.test(url));
  }

  isScormManagerAndOnBlacklist = (ruleSet) => {
    return ruleSet === 'scorm_manager' && this.pathOnScormManagerBlackList();
  }

  redirectToInterstitial = (ruleSet) => {
    const { currentRuleSets = [] } = this.props;
    const redirect = currentRuleSets.find(currentRuleSet => currentRuleSet.name === ruleSet)?.interstitial_redirect;
    if (redirect) {
      window.location.href = redirect;
    }
  }

  onSelectRuleSet = ruleSet => {
    if (this.pathIsWhitelisted() || this.isScormManagerAndOnBlacklist(ruleSet)) {
      this.redirectToInterstitial(ruleSet);
    } else {
      // replace the current_rule_set_name query param with the selected ruleSet name
      const params = new URLSearchParams(window.location.search);
      params.set('current_rule_set_name', ruleSet);
      window.location.search = params.toString();
    }
  }

  handleOutsideClick = evt => {
    // ignore clicks on the component itself
    if (this.node.contains(evt.target)) {
      return;
    }

    this.toggleSidebar();
  }

  toggleSidebar = () => {
    if (window.innerWidth < EXPANDED_SIDEBAR_BREAKPOINT) {
      if (this.state.sidebarExpanded && this.state.menuData) {
        this.state.menuData.submenu_items.map(function (item) {
          item.showChildren = false;
        });
      }

      if (!this.state.sidebarExpanded) {
        document.addEventListener('click', this.handleOutsideClick, false);
      } else {
        document.removeEventListener('click', this.handleOutsideClick, false);
      }
      this.setState({ sidebarExpanded: !this.state.sidebarExpanded });
    }
  }

  onGroupSelect = item => {
    if (item.submenu_items && item.submenu_items.length > 0) {
      item.showChildren = !item.showChildren;
      this.toggleSidebar();
      this.forceUpdate();
    } else {
      if (item.target_url) {
        if (item.new_window) {
          this.windowHelper.open(item.target_url, '_blank');
        } else {
          this.setCurrentUrl(item.target_url);
          this.windowHelper.redirect(item.target_url);
        }
      }
    }
  }

  onRoleSwitcherOpen = () => {
    if (!this.state.sidebarExpanded) {
      this.toggleSidebar();
      this.forceUpdate();
    }
  }

  setCurrentUrl = url => {
    let menuData = cloneDeep(this.state.menuData);
    menuData.submenu_items.forEach(i => { i.showChildren = false; });
    this.setState({ currentUrl: url, menuData });
  }

  render() {
    return (
      <nav ref={node => { this.node = node; }} id="sidebar" className={"nav sidebar" + (this.state.sidebarExpanded ? " show-labels" : "")} role="navigation">
        <SidebarRoleSwitcher
          currentRuleSets={this.props.currentRuleSets}
          onMenuOpen={this.onRoleSwitcherOpen}
          onSelectRuleSet={this.onSelectRuleSet}
        />
        <div className="sidebar-menu" id="side-menu">
          {this.state.menuData.submenu_items.map((item) =>
            <SidebarMenuGroup
              key={item.title}
              currentUrl={this.state.currentUrl}
              item={item}
              onSelect={this.onGroupSelect}
              setCurrentUrl={this.setCurrentUrl}
              serverSide={this.state.serverSide}
              sidebarExpanded={this.state.sidebarExpanded}
            />
          )}
        </div>
        <div className="toggle-control" onClick={this.toggleSidebar} />
      </nav>
    );
  }
}
